import type { ReactElement } from 'react';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { styled } from '@compiled/react';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { Button, Link } from '@helpcenter/analytics-components';
import { HtmlAnchor } from '@helpcenter/analytics-components/src/html-anchor';
import { ThemedBadge as AvatarBadge } from '@helpcenter/badges';
import type { LogOutAction } from '@helpcenter/common-actions';
import { initializeConnect } from '@helpcenter/connect-fragment';
import {
	EngagementMessages,
	HELP_CENTERS_MANAGEMENT_BASE_PATH,
	EngagementMessagesTarget,
	ScreenName,
} from '@helpcenter/constants';
import { EngagementCoordinationClient } from '@helpcenter/engagement-coordination-client';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import {
	isWrmOptimizationsEnabled,
	isUnifiedHelpReportingPageEnabled,
	isCustomPagesUiEnabled,
	isHideVsaEndpointsEnabled,
	isCsmAppification,
} from '@helpcenter/feature-flags';
import { ForgeActionModule } from '@helpcenter/forge-action';
import type { SetExtensionToDisplayType } from '@helpcenter/forge-ui-utils';
import {
	USER_MENU_ACTION_COMPONENT,
	USER_MENU_ACTION_SECTION_MODULE,
} from '@helpcenter/forge-ui-utils/src/constants';
import { ForgeModalContext } from '@helpcenter/forge-ui-utils/src/modal-context';
import { ForgeModalContextProvider } from '@helpcenter/forge-ui-utils/src/modal-context-provider';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import { PremiumEnterpriseAdminsChangeBoarding } from '@helpcenter/premium-enterprise-admins-changeboarding';
import { RenderNextAnimationFrame } from '@helpcenter/render-next-animation-frame';
import * as grid from '@helpcenter/styles/src/grid';
import { sizes } from '@helpcenter/styles/src/sizes-viewport';
import { VaOnboardingSpotlight } from '@helpcenter/va-reporting-onboarding';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useRelayEnvironment } from 'react-relay';
import { useRouter } from 'react-resource-router';
import type { Column } from 'rest/requests-column-settings';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Profile } from '@atlaskit/atlassian-navigation';
import Avatar from '@atlaskit/avatar';
import FeatureGates from '@atlaskit/feature-gate-js-client';

// Wrapped function to allow mocking in tests
export const isRovoAgentFeatureEnabled = () =>
	FeatureGates.checkGate('jsm_help_center_one-click_rovo_agent');

import Lozenge from '@atlaskit/lozenge';
import { CustomItem, HeadingItem, MenuGroup, Section } from '@atlaskit/menu';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import Popup from '@atlaskit/popup';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FireUIAnalytics } from '@atlassian/analytics-bridge';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
// eslint-disable-next-line no-restricted-imports
import { HelpHelpCenterAri } from '@atlassian/ari';
// eslint-disable-next-line rulesdir/fedramp-isolated-cloud
import { isFedRamp } from '@atlassian/atl-context';
import { PremiumBenefitModalTrigger } from '@atlassian/edition-benefits-modal';
import type { TouchpointId, Edition } from '@atlassian/edition-benefits-modal';
import { MODAL_CONFIG } from '@atlassian/edition-benefits-modal/constants';
import { editionBenefitsModalEntryPoint } from '@atlassian/edition-benefits-modal/edition-benefits-modal-entrypoint';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import {
	isAdvancedHelpCenter,
	isCSMHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import type { Env } from '@atlassian/help-center-common-util/env';
import {
	getEnv,
	getIsHCCustomizationEnabled,
	getIsMultiHCEnabled,
} from '@atlassian/help-center-common-util/env';
import { getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import { clearSidebarExperienceAndCustomize } from '@atlassian/help-center-common-util/sidebar-experience';
import { withDeferredWrmLoaded } from '@atlassian/help-center-common-util/wrm';
import { useQueryParams } from '@atlassian/help-center-search/src/common';
import { ExperienceSuccess, useUFOComponentExperience } from '@atlassian/ufo';
import { AccountDetails } from '../account-details';
import { MultiHcDropdown } from '../multi-hc-dropdown';
import { AvatarMenuExperienceFailure, avatarMenuExperience } from './experiences';
import messages from './messages';
import { RovoAgentMenu } from './rovo-agent-menu';
import { VirtualAgentMenu } from './virtual-agent-menu';

export interface CustomMenuLinkProps {
	id: string | undefined;
	key: string | undefined;
	label: string | undefined;
	href: string;
	styleClass: string | undefined;
}

interface Portal {
	id?: number;
	name?: string;
	isProjectSimplified: boolean;
	isProjectAdmin: boolean;
	canEditAnnouncement: boolean;
}
export interface StateProps {
	avatarSrc: string | undefined;
	displayName: string;
	email: string;
	customMenuLinks: CustomMenuLinkProps[];
	areCustomerServiceSettingsLinksAvailable: boolean;
	routeName?: string;
	portal?: Portal;
	pendingApprovalCount?: number;
	approvalCount?: number;
	canAdministerJIRA: boolean;
	hasSiteAdministerAccess?: boolean;
	canCustomiseHelpCenter?: boolean;
	myRequestCount?: number;
	defaultColumnSettings?: Column[];
	jsmAIConfigEnabled?: boolean;
	isOpen?: boolean;
	disableAvatarMenu?: boolean;
	hoistedProjectKey?: string;
	jiraDisplayUrl?: string;
}

export interface DispatchProps {
	onLogoutClick: () => LogOutAction;
	openHelpCenterCustomizationSidebar: () => void;
	openRequestListCustomizationSidebar: () => void;
}

export type Props = StateProps & DispatchProps;
interface ForgeTitleProps {
	onClose: (actionSubjectId?: string) => void;
}

export const ForgeTitleMenuLinks = ({ onClose }: ForgeTitleProps) => {
	const {
		data: { extensionTitles },
		methods: { setExtensionToDisplay: onClick },
	} = React.useContext(ForgeModalContext);

	const forgeActionLinkOnClick = (id: string, onClickApp: SetExtensionToDisplayType) => () => {
		onClose('navBarForgeApp');
		return onClickApp(id);
	};
	return (
		<>
			{extensionTitles.map(({ id, title }) => {
				return (
					<CustomItem
						id={id}
						key={`forge-title_${id}`}
						component={CustomButton}
						actionSubjectId="navBarForgeApp"
						onClick={forgeActionLinkOnClick(id, onClick)}
					>
						{title}
					</CustomItem>
				);
			})}
		</>
	);
};

const menuBottomSection = (
	onClose: (actionSubjectId?: string) => void,
	isHelpCenterManagementScreen: boolean,
	onLogoutClick: () => LogOutAction,
) => {
	return (
		<Section hasSeparator>
			{isHelpCenterManagementScreen ? (
				<CustomItem
					actionSubjectId="navbarProfile"
					key={`bottom-menu-management-screen-navbarProfile`}
					component={CustomAnchor}
					href={'/servicedesk/customer/user/profile'}
					onClick={() => onClose('navbarProfile')}
					data-test-id="profile-link"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
					className="cv-exclude-from-customisation"
				>
					<FormattedMessage {...messages.profile} />
				</CustomItem>
			) : (
				<CustomItem
					key={`bottom-menu-navbarProfile`}
					component={CustomLink}
					to={'/user/profile'}
					actionSubjectId="navbarProfile"
					onClick={() => onClose('navbarProfile')}
					data-test-id="profile-link"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
					className="cv-exclude-from-customisation"
				>
					<FormattedMessage {...messages.profile} />
				</CustomItem>
			)}
			<AnalyticsContext data={{ actionSubjectId: 'navbarLogout' }}>
				<CustomItem
					component={CustomButton}
					actionSubjectId="navbarLogout"
					// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
					aria-label={'logout'}
					onClick={onLogoutClick}
					data-test-id="logout-link"
				>
					<FormattedMessage {...messages.logout} />
				</CustomItem>
			</AnalyticsContext>
		</Section>
	);
};

export const CustomMenuLinks = ({
	customMenuLinks,
	onClose,
}: {
	customMenuLinks: CustomMenuLinkProps[];
	onClose: (actionSubjectId?: string) => void;
}) => (
	<span data-test-id="custom-menu-link">
		{customMenuLinks.map((menuLink) => (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
			<div key={`custom-menu-link_${menuLink.key || ''}`} className={menuLink.styleClass}>
				<CustomItem
					actionSubjectId={'custom-menu-link'}
					component={CustomAnchor}
					href={menuLink.href}
					onClick={() => onClose('custom-menu-link')}
					data-test-id="custom-menu-link"
					key={`custom-menu-link-id_${menuLink.id || ''}`}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
					className="cv-exclude-from-customisation"
				>
					{menuLink.label}
				</CustomItem>
			</div>
		))}
	</span>
);

const buildCustomMenuLinks = (
	customMenuLinks: CustomMenuLinkProps[],
	onClose: (actionSubjectId?: string) => void,
) => {
	return customMenuLinks.map((menuLink) => (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
		<div key={`build-custom-menu-link_${menuLink.key || ''}`} className={menuLink.styleClass}>
			<CustomItem
				actionSubjectId={'custom-menu-link'}
				component={CustomAnchor}
				data-test-id="custom-menu-link"
				key={`custom-menu-link-id_${menuLink.id || ''}`}
				onClick={() => onClose('custom-menu-link')}
				href={menuLink.href}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
				className="cv-exclude-from-customisation"
			>
				{menuLink.label}
			</CustomItem>
		</div>
	));
};

const TopMenu = (
	myRequestCount: number,
	onClose: (actionSubjectId?: string) => void,
	pendingApprovalCount: number,
	approvalCount: number,
	isHelpCenterManagementScreen: boolean,
) => {
	const customizeMenu = [
		{
			onClick: () => onClose('navbarUserRequests'),
			link: isHelpCenterManagementScreen
				? '/servicedesk/customer/user/requests?reporter=all&statuses=open'
				: '/user/requests?reporter=all&statuses=open',
			label: <FormattedMessage {...messages.requests} />,
			actionSubjectId: 'navbarUserRequests',
			iconAfter: myRequestCount > 0 ? <AvatarBadge>{myRequestCount}</AvatarBadge> : undefined,
			isVisible: !isCSMHelpCenter(getEnv().helpCenterType),
			dataTestId: 'requests-link',
		},
		{
			onClick: () => onClose('navbarUserApprovals'),
			link: isHelpCenterManagementScreen
				? '/servicedesk/customer/user/approvals'
				: '/user/approvals',
			label: <FormattedMessage {...messages.approvals} />,
			actionSubjectId: 'navbarUserApprovals',
			iconAfter:
				pendingApprovalCount > 0 ? <AvatarBadge>{pendingApprovalCount}</AvatarBadge> : undefined,
			isVisible: approvalCount > 0 || pendingApprovalCount > 0,
			dataTestId: 'approvals-link',
		},
	];
	return (
		<Section>
			{customizeMenu.map((menu) => {
				if (!menu.isVisible) {
					return null;
				}
				return (
					<>
						{isHelpCenterManagementScreen ? (
							<CustomItem
								actionSubjectId={menu.actionSubjectId}
								key={`top-menu-management-screen_${menu.actionSubjectId}`}
								component={CustomAnchor}
								href={menu.link}
								onClick={menu.onClick}
								iconAfter={menu.iconAfter}
								data-test-id={menu.dataTestId}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
								className="cv-exclude-from-customisation"
							>
								{menu.label}
							</CustomItem>
						) : (
							<CustomItem
								key={`top-menu_${menu.actionSubjectId}`}
								component={CustomLink}
								to={menu.link}
								actionSubjectId={menu.actionSubjectId}
								onClick={menu.onClick}
								iconAfter={menu.iconAfter}
								data-test-id={menu.dataTestId}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
								className="cv-exclude-from-customisation"
							>
								{menu.label}
							</CustomItem>
						)}
					</>
				);
			})}
		</Section>
	);
};

interface CustomizeMenuItem {
	link?: string;
	label: ReactElement;
	customRenderer?: (props: { menu: CustomizeMenuItem }) => ReactElement;
	actionSubjectId: string;
	isVisible: boolean | undefined;
	pbmTouchpointId?: TouchpointId;
	onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
	testId?: string;
	isAnchor?: boolean;
	isBeta?: boolean;
	isNew?: boolean;
	showSpotlight?: boolean;
	isPremium?: boolean;
	messageDescription?: MessageDescriptor;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	component?: React.ComponentType<any>;
}

const CustomMenuItems = ({ menu }: { menu: CustomizeMenuItem }) => {
	const { formatMessage } = useIntl();
	return (
		<>
			<CustomItem
				component={menu.component ?? CustomLink}
				to={menu.link}
				actionSubjectId={menu.actionSubjectId}
				onClick={menu.onClick}
				testId={menu.testId}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
				className="cv-exclude-from-customisation"
				iconAfter={
					<>
						{menu.isBeta && (
							<Lozenge appearance="discovery">{formatMessage(messages.beta)}</Lozenge>
						)}
						{menu.isNew && (
							<Lozenge appearance="discovery">{formatMessage(messages.newLozenge)}</Lozenge>
						)}
						{menu.isPremium && (
							<Lozenge appearance="discovery">{formatMessage(messages.premiumLozenge)}</Lozenge>
						)}
					</>
				}
			>
				{menu.label}
			</CustomItem>
			{menu.actionSubjectId === 'createHelpCenter' && (
				<FireUIAnalytics action="viewed" actionSubject={menu.actionSubjectId} />
			)}
		</>
	);
};

const CustomPressable = ({
	children,
	...rest
}: {
	children?: React.ReactNode;
	onClick?: () => void;
}) => (
	<Pressable componentName="link" xcss={customPressableStyles} {...rest}>
		{children}
	</Pressable>
);

/**
 * Feature gate: premium_benefits_modal_config_driven
 *
 * When enabled, open the new config-driven edition-benefits-modal via its async
 * entrypoint (`editionBenefitsModalEntryPoint` + `ModalTrigger`) instead of the
 * legacy `PremiumBenefitModalTrigger`. The entrypoint loads on demand, keeping
 * the modal's assets out of the initial bundle.
 *
 * Rollout: Pending — gate created 2026-07-15, default off
 * Target: TBD
 * Owner: Sequoia (SQA-4082)
 */
const PREMIUM_BENEFITS_MODAL_CONFIG_DRIVEN = 'premium_benefits_modal_config_driven';
const isPremiumBenefitsModalConfigDrivenEnabled = (): boolean =>
	FeatureGates.checkGate(PREMIUM_BENEFITS_MODAL_CONFIG_DRIVEN);

const PremiumBenefitsModalRenderer = ({ menu }: { menu: CustomizeMenuItem }) => {
	di(useAnalyticsEvents);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isConfigDriven = isPremiumBenefitsModalConfigDrivenEnabled();
	const edition = ((getEnv() as Env & { edition?: string }).edition ?? 'unknown') as
		| Edition
		| 'unknown';

	// The config-driven ModalTrigger path doesn't emit the trigger analytics that the
	// legacy PremiumBenefitModalTrigger did, so replicate them here for parity.
	const fireTriggerEvent = useCallback(
		(action: 'viewed' | 'clicked') =>
			createAnalyticsEvent({
				action,
				eventType: 'ui',
				actionSubject: 'modalTrigger',
				actionSubjectId: 'editionBenefitsModalTrigger',
				attributes: {
					edition,
					touchpointId: menu.pbmTouchpointId,
					product: 'jira-service-management',
					variantId: 'help-center-customization',
					packageName: '@atlassian/edition-benefits-modal',
					source: 'editionBenefitsModal',
				},
			}).fire('growth'), // FabricChannel.growth
		[createAnalyticsEvent, edition, menu.pbmTouchpointId],
	);

	const hasFiredViewed = useRef(false);
	useEffect(() => {
		// Fire the trigger "viewed" event once when the config-driven trigger is shown.
		if (isConfigDriven && !hasFiredViewed.current) {
			hasFiredViewed.current = true;
			fireTriggerEvent('viewed');
		}
	}, [isConfigDriven, fireTriggerEvent]);

	const renderTrigger = ({
		ref,
		onTriggerClick,
		props: triggerProps,
	}: {
		ref: React.RefObject<HTMLElement>;
		props: object;
		onTriggerClick?: () => void;
	}) => (
		// CustomItem doesn't forward testId to custom components, so we add it to the Box wrapper
		<Box ref={ref} {...triggerProps} testId={menu.testId}>
			<AnalyticsContext data={{ actionSubjectId: menu.actionSubjectId }}>
				<CustomMenuItems
					menu={{
						...menu,
						component: CustomPressable,
						// Compose the trigger analytics with the item's own handler so the "clicked"
						// event fires on the element itself rather than relying on event bubbling.
						...(onTriggerClick
							? {
									onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
										onTriggerClick();
										menu.onClick?.(event);
									},
								}
							: {}),
					}}
				/>
			</AnalyticsContext>
		</Box>
	);

	return (
		<ErrorBoundary
			packageName={ScreenName.HELP_CENTER}
			id={`help-center-${menu.pbmTouchpointId as string}`}
			renderError={() => null}
		>
			{isConfigDriven ? (
				<ModalTrigger
					entryPoint={editionBenefitsModalEntryPoint}
					entryPointParams={{ cloudId: getCloudId(), product: 'jira-service-management' }}
					entryPointProps={{
						variantId: 'help-center-customization',
						touchpointId: menu.pbmTouchpointId as TouchpointId,
					}}
					modalProps={MODAL_CONFIG}
				>
					{({ ref }) =>
						renderTrigger({ ref, props: {}, onTriggerClick: () => fireTriggerEvent('clicked') })
					}
				</ModalTrigger>
			) : (
				<PremiumBenefitModalTrigger
					cloudId={getCloudId()}
					product="jira-service-management"
					variantId="help-center-customization"
					touchpointId={menu.pbmTouchpointId as TouchpointId}
					edition={edition}
				>
					{renderTrigger}
				</PremiumBenefitModalTrigger>
			)}
		</ErrorBoundary>
	);
};

const ListItemWrapper = ({ children }: { children: ReactElement }) => {
	return (
		<Box as="li" xcss={listItemStyles}>
			{children}
		</Box>
	);
};

export const ItemsContainer = ({ customizeMenu = [] }: { customizeMenu: CustomizeMenuItem[] }) => {
	di(
		getIsHCCustomizationEnabled,
		isUnifiedHelpReportingPageEnabled,
		VaOnboardingSpotlight,
		EngagementCoordinationClient,
		RenderNextAnimationFrame,
	);
	const { formatMessage } = useIntl();
	const [showVaReportingOnboardingSpotlight, setShowVaReportingOnboardingSpotlight] =
		useState(true);
	return (
		<>
			{customizeMenu.map((menu) => {
				if (!menu.isVisible) {
					return null;
				}
				if (menu.isAnchor) {
					return (
						<ListItemWrapper key={`customize-menu_${menu.actionSubjectId}`}>
							<CustomItem
								component={CustomAnchor}
								href={menu.link}
								actionSubjectId={menu.actionSubjectId}
								onClick={menu.onClick}
								testId={menu.testId}
								iconAfter={
									menu.isBeta && (
										<Lozenge appearance="discovery">{formatMessage(messages.beta)}</Lozenge>
									)
								}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
								className="cv-exclude-from-customisation"
							>
								{menu.label}
							</CustomItem>
						</ListItemWrapper>
					);
				}

				if (menu.showSpotlight) {
					return (
						<ListItemWrapper key={`customize-menu_spotlight_${menu.actionSubjectId}`}>
							<>
								<RenderNextAnimationFrame>
									{() => (
										<SpotlightTarget name={EngagementMessagesTarget.REPORTING_ONBOARDING_SPOTLIGHT}>
											<CustomItem
												key={`customize-menu_${menu.actionSubjectId}`}
												component={CustomLink}
												to={menu.link}
												actionSubjectId={menu.actionSubjectId}
												onClick={menu.onClick}
												testId={menu.testId}
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
												className="cv-exclude-from-customisation"
												iconAfter={
													<>
														{menu.isBeta && (
															<Lozenge appearance="discovery">
																{formatMessage(messages.beta)}
															</Lozenge>
														)}
														{menu.isNew && (
															<Lozenge appearance="discovery">
																{formatMessage(messages.newLozenge)}
															</Lozenge>
														)}
													</>
												}
											>
												{menu.label}
											</CustomItem>
										</SpotlightTarget>
									)}
								</RenderNextAnimationFrame>
								{showVaReportingOnboardingSpotlight && (
									<SpotlightTransition>
										<EngagementCoordinationClient
											messageId={EngagementMessages.ONBOARD_VA_REPORTING}
											key={EngagementMessagesTarget.REPORTING_ONBOARDING_SPOTLIGHT}
										>
											<VaOnboardingSpotlight
												setShowVaReportingOnboardingSpotlight={
													setShowVaReportingOnboardingSpotlight
												}
											/>
										</EngagementCoordinationClient>
									</SpotlightTransition>
								)}
							</>
						</ListItemWrapper>
					);
				}
				if (menu.customRenderer) {
					return (
						<ListItemWrapper key={`customize-menu_custom_${menu.actionSubjectId}`}>
							<menu.customRenderer menu={menu} />
						</ListItemWrapper>
					);
				}
				return (
					<ListItemWrapper key={`customize-menu_${menu.actionSubjectId}`}>
						<CustomItem
							component={CustomLink}
							to={menu.link}
							actionSubjectId={menu.actionSubjectId}
							onClick={menu.onClick}
							testId={menu.testId}
							iconAfter={
								<>
									{menu.isBeta && (
										<Lozenge appearance="discovery">{formatMessage(messages.beta)}</Lozenge>
									)}
									{menu.isNew && (
										<Lozenge appearance="discovery">{formatMessage(messages.newLozenge)}</Lozenge>
									)}
								</>
							}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
							className="cv-exclude-from-customisation"
						>
							{menu.label}
						</CustomItem>
					</ListItemWrapper>
				);
			})}
		</>
	);
};

const CustomizeLinks = (
	onClose: (actionSubjectId?: string) => void,
	openHelpCenterCustomizationSidebar: DispatchProps['openHelpCenterCustomizationSidebar'],
	canAdministerJIRA: boolean,
	routeName: string | undefined,
	basePath: string,
	areCustomerServiceSettingsLinksAvailable: boolean,
	showAvatarMenuItemsUpsells: boolean,
	hoistedProjectKey?: string,
	jiraDisplayUrl?: string,
	isServiceHub?: boolean,
) => {
	di(
		getIsHCCustomizationEnabled,
		isUnifiedHelpReportingPageEnabled,
		VaOnboardingSpotlight,
		EngagementCoordinationClient,
		RenderNextAnimationFrame,
		token,
		isCSMHelpCenter,
	);
	const isHomePage = routeName === 'help-center' || routeName === 'view-homepage';

	const customizeMenu: CustomizeMenuItem[] = [];

	const helpCenterId = HelpHelpCenterAri.parse(getHelpCenterAri()).helpCenterId;

	const isVisible =
		areCustomerServiceSettingsLinksAvailable &&
		!!jiraDisplayUrl &&
		!!hoistedProjectKey &&
		!!helpCenterId &&
		(isHomePage || isServiceHub) &&
		canAdministerJIRA;

	const helpCenterSettingsUrl = isCsmAppification()
		? `${jiraDisplayUrl ?? ''}/jira/customer-service-management/experiences/${
				hoistedProjectKey ?? ''
			}/settings/${helpCenterId}/details`
		: `${jiraDisplayUrl ?? ''}/jira/helpcenter/projects/${
				hoistedProjectKey ?? ''
			}/settings/${helpCenterId}/details`;

	const additionalMenuItems = [
		{
			isVisible,
			isAnchor: true,
			link: helpCenterSettingsUrl,
			label: <FormattedMessage {...messages.customerHubSettings} />,

			actionSubjectId: 'helpCenterSettings',
			testId: 'help-center-settings',
		},
	];
	customizeMenu.push(...additionalMenuItems);

	const hideSection = () => customizeMenu.every((item) => !item.isVisible);

	if (hideSection()) return null;

	return <ItemsContainer customizeMenu={customizeMenu} />;
};

interface RenderHCSectionProps {
	onClick: (actionSubjectId?: string) => void;
	showAvatarMenuItemsUpsells: boolean;
}

const RenderHCSection = ({ onClick, showAvatarMenuItemsUpsells }: RenderHCSectionProps) => {
	di(isCustomPagesUiEnabled, getHelpCenterAri, getEnv);
	const { formatMessage } = useIntl();
	const { helpCenterId } = HelpHelpCenterAri.parse(getHelpCenterAri());
	const customizeMenu: CustomizeMenuItem[] = [
		{
			link: `${HELP_CENTERS_MANAGEMENT_BASE_PATH}`,
			label: <FormattedMessage {...messages.manageHc} />,
			actionSubjectId: 'manageHelpCenter',
			isVisible: true,
			onClick: () => onClick('manageHelpCenter'),
			testId: 'manage-hc-link',
		},
		{
			link: getIsMultiHCEnabled() ? `${HELP_CENTERS_MANAGEMENT_BASE_PATH}?create=true` : '#',
			label: <FormattedMessage {...messages.createHc} />,
			actionSubjectId: 'createHelpCenter',
			isVisible: showAvatarMenuItemsUpsells,
			pbmTouchpointId: 'portal-avatar-menu-create-help-center' as TouchpointId,
			customRenderer: getIsHCCustomizationEnabled() ? undefined : PremiumBenefitsModalRenderer,
			onClick: () => onClick('createHelpCenter'),
			testId: 'create-hc-link',
			isPremium: !getIsHCCustomizationEnabled(),
		},
		...(isCustomPagesUiEnabled()
			? [
					{
						label: <FormattedMessage {...messages.managePages} />,
						actionSubjectId: 'manageHelpCenterPages',
						isVisible: showAvatarMenuItemsUpsells,
						pbmTouchpointId: 'portal-avatar-menu-landing-pages' as TouchpointId,
						onClick: () => onClick('manageHelpCenterPages'),
						testId: 'manage-hc-pages',
						link: `${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/pages`,
						isPremium: !getIsHCCustomizationEnabled(),
						customRenderer: getIsHCCustomizationEnabled()
							? undefined
							: PremiumBenefitsModalRenderer,
						showSpotlight: false,
						messageDescription: messages.landingPagesNonPremiumDescription,
					},
				]
			: []),
	];

	return (
		<>
			<HeadingItem id="avatar-menu-hc-section-heading">
				{formatMessage(messages.helpCenter)}
			</HeadingItem>
			{customizeMenu.map((menu) => {
				if (!menu.isVisible) {
					return null;
				}
				if (menu.customRenderer) {
					return (
						<ListItemWrapper key={`customize-menu_${menu.actionSubjectId}`}>
							<>
								<menu.customRenderer menu={menu} />
								{menu.actionSubjectId === 'manageHelpCenterPages' && (
									<FireUIAnalytics action="viewed" actionSubject={menu.actionSubjectId} />
								)}
							</>
						</ListItemWrapper>
					);
				}
				const renderMenuItem = () => (
					<ListItemWrapper key={`hc-section-menu_${menu.actionSubjectId}`}>
						<>
							<CustomItem
								component={CustomAnchor}
								href={menu.link}
								actionSubjectId={menu.actionSubjectId}
								onClick={menu.onClick}
								testId={menu.testId}
								iconAfter={
									menu.isPremium && (
										<Lozenge appearance="discovery">
											{formatMessage(messages.premiumLozenge)}
										</Lozenge>
									)
								}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
								className="cv-exclude-from-customisation"
							>
								{menu.label}
							</CustomItem>
							{menu.actionSubjectId === 'createHelpCenter' && (
								<FireUIAnalytics action="viewed" actionSubject={menu.actionSubjectId} />
							)}
						</>
					</ListItemWrapper>
				);

				if (menu.showSpotlight) {
					return (
						<RenderNextAnimationFrame delay={100} key={menu.actionSubjectId}>
							{() => renderMenuItem()}
						</RenderNextAnimationFrame>
					);
				}
				return renderMenuItem();
			})}
		</>
	);
};

const UserProfileConnectActionLinks = withDeferredWrmLoaded(CustomMenuLinks, initializeConnect);

const AvatarMenuDumbV2 = (props: Props) => {
	di(
		ForgeModalContextProvider,
		ForgeActionModule,
		useAnalyticsEvents,
		getRelativeBasePath,
		isAdvancedHelpCenter,
		useUFOComponentExperience,
		ExperienceSuccess,
		useQueryParams,
		EngagementCoordinationClient,
		MultiHcDropdown,
		PremiumEnterpriseAdminsChangeBoarding,
		useRouter,
		isUnifiedHelpReportingPageEnabled,
		isFedRamp,
		isWrmOptimizationsEnabled,
		isCSMHelpCenter,
		isRovoAgentFeatureEnabled,
		isCsmAppification,
	);

	// Reading the from useRouter even thought we are getting from props because
	// there is an unneccessary rerender happening in the component which is causing the Engagement client
	// to make an unnecessary start call
	// Keep useRouter hook call to preserve hook ordering
	useRouter();
	const isHomepage = props?.routeName === 'help-center' || props?.routeName === 'view-homepage';
	useUFOComponentExperience(avatarMenuExperience);
	const isHelpCenterManagementScreen = getRelativeBasePath() === HELP_CENTERS_MANAGEMENT_BASE_PATH;
	const basePath = isAdvancedHelpCenter() ? '/' : '/portals';
	const [isOpen, setIsOpen] = useState(!!props.isOpen);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const { customize, experience } = useQueryParams();
	const { formatMessage } = useIntl();
	// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-in-react-fc
	const isSmallScreen = window.innerWidth < sizes.xsmall;

	// Keep the hook call to preserve hook ordering
	useRelayEnvironment();

	const onOpenChange = useCallback(
		(open: boolean) => {
			setIsOpen(open);
			// if customize is true and experience is not set, clear customize from the queryParam
			if (!open && customize === 'true' && !experience) {
				clearSidebarExperienceAndCustomize();
			}
		},
		[customize, experience],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onItemClick = (actionSubjectId?: string) => {
		if (actionSubjectId && createAnalyticsEvent) {
			createAnalyticsEvent({
				actionSubjectId,
				action: 'clicked',
				actionSubject: 'avatarMenu',
				version: '2',
				analyticsType: UI_EVENT_TYPE,
			}).fire();
		}
		setIsOpen(false);
	};

	// This state is used to track if the landing pages changeboarding spotlight has been viewed so that the avatar menu is not opened again on state change within same session of seeing change boarding spotlight.
	const [_, setAvatarMenuOpenedOnceForChangeboarding] = useState(false);
	const content = () => {
		const {
			myRequestCount,
			customMenuLinks,
			onLogoutClick,
			openHelpCenterCustomizationSidebar,
			routeName,
			pendingApprovalCount,
			approvalCount,
			canAdministerJIRA,
			hasSiteAdministerAccess,
			canCustomiseHelpCenter,
			avatarSrc,
			displayName,
			email,
			hoistedProjectKey,
			areCustomerServiceSettingsLinksAvailable,
			jiraDisplayUrl,
		} = props;

		const isServiceHub = !!hoistedProjectKey;
		const virtualAgentAllowed = isFeatureEnabled(
			'jira-service-management',
			'jsm-help-center-virtual-agent',
		);
		const showAvatarMenuItemsUpsells =
			getIsHCCustomizationEnabled() || Boolean(hasSiteAdministerAccess);
		const showVirtualAgentMenu =
			!isHideVsaEndpointsEnabled() &&
			canAdministerJIRA &&
			!isServiceHub &&
			!isSmallScreen &&
			!isCSMHelpCenter(getEnv().helpCenterType) &&
			virtualAgentAllowed &&
			(!hasSiteAdministerAccess || getIsHCCustomizationEnabled());
		// Feature gate: jsm_help_center_one-click_rovo_agent
		// Rovo Agent menu is independent of VSA settings
		const rovoAgentAllowed = isFeatureEnabled(
			'jira-service-management',
			'jsm-help-center-rovo-agent',
		);
		const showRovoAgentMenu =
			canAdministerJIRA &&
			!isServiceHub &&
			!isSmallScreen &&
			!isCSMHelpCenter(getEnv().helpCenterType) &&
			rovoAgentAllowed;

		return (
			<Wrapper aria-label={formatMessage(messages.tooltip)} data-testid="avatar-menu-content">
				<MenuGroup maxHeight={maxHeight} spacing={'cozy'}>
					{<AccountDetails avatarSrc={avatarSrc} displayName={displayName} email={email} />}
					{TopMenu(
						myRequestCount || 0,
						onItemClick,
						pendingApprovalCount || 0,
						approvalCount || 0,
						isHelpCenterManagementScreen,
					)}
					{!isHelpCenterManagementScreen && (
						<>
							{canAdministerJIRA && (
								<Section hasSeparator={!isSmallScreen}>
									<Box
										as="ul"
										xcss={listItemStyles}
										key="help-center-menu"
										// @ts-ignore - aria-label is valid for ul elements
										aria-label={formatMessage(messages.helpCenterMenuOptions)}
									>
										{!isSmallScreen && !isServiceHub && (
											<RenderHCSection
												showAvatarMenuItemsUpsells={showAvatarMenuItemsUpsells}
												onClick={onItemClick}
											/>
										)}
										{canCustomiseHelpCenter &&
											(isHomepage || isServiceHub) &&
											CustomizeLinks(
												onItemClick,
												openHelpCenterCustomizationSidebar,
												canAdministerJIRA,
												routeName,
												basePath,
												areCustomerServiceSettingsLinksAvailable,
												showAvatarMenuItemsUpsells,
												hoistedProjectKey,
												jiraDisplayUrl,
												isServiceHub,
											)}
										{showRovoAgentMenu && (
											<ListItemWrapper key="customize-menu_rovo_agent">
												<RovoAgentMenu onClose={onItemClick} component={CustomAnchor} />
											</ListItemWrapper>
										)}
										{showVirtualAgentMenu && (
											<ListItemWrapper key="customize-menu_virtual_agent">
												<VirtualAgentMenu onClose={onItemClick} component={CustomAnchor} />
											</ListItemWrapper>
										)}
									</Box>
								</Section>
							)}
							{customMenuLinks && customMenuLinks.length > 0 ? (
								<Section hasSeparator={customMenuLinks && customMenuLinks.length > 0}>
									{isWrmOptimizationsEnabled() ? (
										<UserProfileConnectActionLinks
											customMenuLinks={customMenuLinks}
											onClose={onItemClick}
										/>
									) : (
										buildCustomMenuLinks(customMenuLinks, onItemClick)
									)}
								</Section>
							) : undefined}
							<ForgeTitleMenuLinks onClose={onItemClick} />
						</>
					)}
					{menuBottomSection(onItemClick, isHelpCenterManagementScreen, onLogoutClick)}
				</MenuGroup>
				{/* Fire success if user data has loaded this should give better TTI and FMP numbers */}
				{displayName && <ExperienceSuccess experience={avatarMenuExperience} />}
			</Wrapper>
		);
	};

	const Trigger = useCallback(
		(triggerProps: Partial<TriggerProps>) => {
			di(isCustomPagesUiEnabled, isCSMHelpCenter);
			const {
				avatarSrc,
				disableAvatarMenu = false,
				pendingApprovalCount = 0,
				myRequestCount = 0,
			} = props;
			const totalRequests = pendingApprovalCount + myRequestCount;

			return (
				<>
					{/* As request menu item is invisible, hiding the badge for CSM is a bug-fix,
                        merging the fix with the flag closely representing M3 */}
					{!isCSMHelpCenter(getEnv().helpCenterType) && totalRequests > 0 && (
						<StyledAvatarBadge>{totalRequests}</StyledAvatarBadge>
					)}
					<ProfileContainer disabled={disableAvatarMenu}>
						<SpotlightTarget name={EngagementMessagesTarget.CHANGE_BOARDING_SPOTLIGHT}>
							<Profile
								{...triggerProps}
								isDisabled={disableAvatarMenu}
								icon={
									<Avatar src={avatarSrc} borderColor={token('color.border.inverse', '#FFFFFF')} />
								}
								isSelected={isOpen}
								onClick={() => {
									onOpenChange(!isOpen);
								}}
								aria-label={formatMessage(messages.tooltip)}
								tooltip={formatMessage(messages.tooltip)}
								testId="user-avatar-container-2"
							/>
						</SpotlightTarget>
					</ProfileContainer>
				</>
			);
		},
		[props, isOpen, formatMessage, onOpenChange],
	);

	const renderDropdownMenu = () => {
		return (
			<SpotlightManager
				blanketIsTinted
				onBlanketClicked={() => setAvatarMenuOpenedOnceForChangeboarding(true)}
			>
				<ModalContextProvider>
					<Popup
						isOpen={isOpen}
						shouldRenderToParent
						onClose={() => onOpenChange(false)}
						placement="bottom-end"
						zIndex={layers.modal()}
						content={content}
						trigger={Trigger}
						// This is required to close the open menu when clicking outside of the menu
						// A complex use case hence attaching a video of the same https://www.loom.com/share/95aa9f0112dd4e028194397e8dfeabea?sid=ef970902-843d-4b62-a578-3ec0f1f1b273
						// shouldUseCaptureOnOutsideClick
						key={'avatar-menu-dumb-v2-popup'}
					/>
				</ModalContextProvider>
			</SpotlightManager>
		);
	};

	const renderError = useCallback((error: Error) => {
		return <AvatarMenuExperienceFailure message={error.message} />;
	}, []);

	return (
		<ErrorBoundary onError={renderError}>
			<AvatarMenuContainer>
				<ForgeModalContextProvider type={USER_MENU_ACTION_SECTION_MODULE}>
					{renderDropdownMenu()}
					<ForgeActionModule
						type={USER_MENU_ACTION_SECTION_MODULE}
						componentName={USER_MENU_ACTION_COMPONENT}
					/>
				</ForgeModalContextProvider>
			</AvatarMenuContainer>
		</ErrorBoundary>
	);
};

AvatarMenuDumbV2.defaultProps = {
	customMenuLinks: [],
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const AvatarMenuContainer = styled.div({
	paddingRight: token('space.100', '8px'),
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const NoUnderlineLink = styled(Link)({
	textDecoration: 'none',
	'&:focus, &:hover, &:visited, &:link, &:active': {
		textDecoration: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Wrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	minWidth: grid.multiple(29.25).px,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	maxWidth: grid.multiple(100).px,
	color: token('color.text', '#172B4D'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const StyledAvatarBadge = styled(AvatarBadge)({
	position: 'absolute',
	left: 0,
	bottom: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	zIndex: layers.navigation(),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const CustomLink = styled(NoUnderlineLink)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	color: 'unset !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&:focus-visible:focus': {
		boxShadow: `inset 0px 0px 0px 2px ${token('color.border.focused', '#4C9AFF')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const CustomButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	color: `${token('color.text', '#172B4D')} !important`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	background: 'unset !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	width: '100% !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	alignItems: 'center !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	padding: '8px 16px !important',
	'&:focus, &:hover, &:visited, &:link, &:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
		color: `${token('color.text', '#172B4D')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
		background: `${token('color.background.neutral.subtle.hovered', '#EBECF0')} !important`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const CustomAnchor = styled(HtmlAnchor)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	color: 'unset !important',
	textDecoration: 'none',
	'&:focus, &:hover, &:visited, &:link, &:active': {
		textDecoration: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&:focus-visible:focus': {
		boxShadow: `inset 0px 0px 0px 2px ${token('color.border.focused', '#4C9AFF')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ProfileContainer = styled.div({
	// base on disabled prop update pointerEvents
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	pointerEvents: ({ disabled }: { disabled: boolean }) => (disabled ? 'none' : 'auto'),
});

const maxHeight = `calc(100vh - ${grid.multiple(24).px}`;

const customPressableStyles = xcss({
	color: 'color.text',
	backgroundColor: 'color.background.neutral.subtle',
	width: '100%',
	padding: 'space.100',
	paddingInline: 'space.200',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

const listItemStyles = xcss({
	listStyleType: 'none',
	padding: 'space.0',
	margin: 'space.0',
});

export default AvatarMenuDumbV2;
